import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTemplateOrCalculation } from '../../../../../../../../hooks/useTemplateOrCalculation';
import { Column, INFINITE_PAGE_SIZE, InfiniteScrollTable, Text } from '@ydistri/ds';
import {
  CalculationRecordImportItemResponse,
  Condition,
  LogicalOperator,
  Operation,
  RecordImportAction,
} from '@ydistri/api-sdk';
import {
  GetCalculationImportItemsRequest,
  useGetCalculationRecordImportItemsQuery,
} from '../../../apiCalculationRedistributionImport';
import {
  EntityColumns,
  EntityColumnsConfig,
  useProductTableColumnsProvider,
  useStoreTableColumnsProvider,
} from '../../../../../../../../hooks/useEntityTableColumnsProvider';
import { useColumnWidth } from '../../../../../../../../hooks/useColumnWidth';
import ScrolledTableWrapper from '../../../../../../../../components/global/ScrolledTableWrapper/ScrolledTableWrapper';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../../../../../../store';
import { InstructionType } from '../../../redistributionImportTypes';
import RedistributionImportValidatedItemAction from './RedistributionImportValidatedItemAction';
import { computeRemSize } from '@ydistri/theme';

const createActionFilterConditions = (importActionFilters: RecordImportAction[]): Condition[] => {
  const logicalOperator = importActionFilters.length > 1 ? LogicalOperator.Or : LogicalOperator.And;
  return importActionFilters.map(importActionFilter => ({
    fieldName: 'RecordImportActionCode',
    operation: Operation.Eq,
    value: importActionFilter.toString(),
    logicalOperator: logicalOperator,
  }));
};

const getActionTooltip = (
  record: CalculationRecordImportItemResponse,
  instructionType: InstructionType,
): React.ReactNode => {
  const errors = [];

  if (record.product === null) {
    errors.push(`Product ${record.productIdentification} was not identified`);
  }

  if (record.sourceStore === null) {
    errors.push(`Source store ${record.sourceStoreIdentification} was not identified`);
  }

  if (record.targetStore === null) {
    errors.push(`Target store ${record.targetStoreIdentification} was not identified`);
  }

  if (
    instructionType === InstructionType.QUANTITY ||
    instructionType === InstructionType.QUANTITY_DESCRIPTION
  ) {
    if (record.quantity === 0) {
      errors.push(`Quantity is not set or is zero`);
    }
  }

  if (
    instructionType === InstructionType.DESCRIPTION ||
    instructionType === InstructionType.QUANTITY_DESCRIPTION
  ) {
    if (!record.description || record.description.trim().length === 0) {
      errors.push(`Description is not set`);
    }
  }

  if (errors.length > 0) {
    return (
      <Column>
        {errors.map(error => (
          <Text key={error}>{error}</Text>
        ))}
      </Column>
    );
  } else {
    return '';
  }
};

const RedistributionImportItemsTable: React.FC = () => {
  const calculation = useTemplateOrCalculation();
  const productColumnsProvider = useProductTableColumnsProvider();
  const storeColumnsProvider = useStoreTableColumnsProvider();
  const { productIdColumnWidth } = useColumnWidth();

  const importActionFilter = useSelector(
    (state: ReduxState) => state.calculations.redistributionImport.importActionFilter,
  );

  const instructionType = useSelector(
    (state: ReduxState) => state.calculations.redistributionImport.instructionSpecification,
  );

  const [params, setParams] = useState<GetCalculationImportItemsRequest>({
    calculationId: calculation.id,
    skip: 0,
    top: INFINITE_PAGE_SIZE,
  });

  useEffect(() => {
    setParams(prevParams => {
      //other conditions may have been set by the table, we need to keep them
      const otherConditions =
        prevParams.conditions?.filter(
          condition => condition.fieldName !== 'RecordImportActionCode',
        ) ?? [];
      const newConditions = [
        ...otherConditions,
        ...createActionFilterConditions(importActionFilter),
      ];

      return {
        ...prevParams,
        skip: 0,
        top: INFINITE_PAGE_SIZE,
        conditions: newConditions,
      };
    });
  }, [importActionFilter]);

  const { data } = useGetCalculationRecordImportItemsQuery(params);

  const actionCellRenderer = useCallback(
    (value: string, record: CalculationRecordImportItemResponse) => {
      const tooltip = getActionTooltip(record, instructionType);
      return (
        <RedistributionImportValidatedItemAction
          action={record.recordImportActionCode}
          isSourceMissing={!record.sourceSku}
          isTargetMissing={!record.targetSku}
          tooltip={tooltip}
        />
      );
    },
    [instructionType],
  );

  const columns = useMemo(() => {
    const productColumnsConfig: EntityColumnsConfig<CalculationRecordImportItemResponse> = {
      [EntityColumns.ID]: {
        width: productIdColumnWidth,
      },
      [EntityColumns.NAME]: {},
      [EntityColumns.CODE]: {
        width: computeRemSize(100),
      },
    };
    const productColumns =
      productColumnsProvider<CalculationRecordImportItemResponse>(productColumnsConfig);

    const storeColumnsConfig: EntityColumnsConfig<CalculationRecordImportItemResponse> = {
      [EntityColumns.ID]: {
        width: computeRemSize(160),
      },
      [EntityColumns.NAME]: {},
      [EntityColumns.CODE]: {
        width: computeRemSize(100),
      },
    };
    const sourceStoreColumns = storeColumnsProvider<CalculationRecordImportItemResponse>(
      storeColumnsConfig,
      'sourceStore',
      'Source store',
    );

    const targetStoreColumns = storeColumnsProvider<CalculationRecordImportItemResponse>(
      storeColumnsConfig,
      'targetStore',
      'Target store',
    );

    const result = [
      {
        title: 'Sequence',
        dataIndex: 'sequence',
        width: computeRemSize(80),
        key: 'sequence',
      },
      ...productColumns,
      ...sourceStoreColumns,
      ...targetStoreColumns,
    ];

    if (
      instructionType === InstructionType.QUANTITY ||
      instructionType === InstructionType.QUANTITY_DESCRIPTION
    ) {
      result.push({
        title: 'Quantity',
        dataIndex: 'quantity',
        width: computeRemSize(80),
        apiFilterable: true,
        apiFilterType: 'number',
        apiColumnName: 'Quantity',
        key: 'quantity',
      });
    }

    if (
      instructionType === InstructionType.DESCRIPTION ||
      instructionType === InstructionType.QUANTITY_DESCRIPTION
    ) {
      result.push({
        title: 'Description',
        dataIndex: 'description',
        width: computeRemSize(160),
        key: 'description',
      });
    }

    result.push({
      title: 'Action',
      dataIndex: 'recordImportActionCode',
      width: computeRemSize(256),
      render: actionCellRenderer,
    });

    return result;
  }, [
    actionCellRenderer,
    instructionType,
    productColumnsProvider,
    productIdColumnWidth,
    storeColumnsProvider,
  ]);

  const rowClassProvider = useCallback((record: CalculationRecordImportItemResponse) => {
    switch (record.recordImportActionCode) {
      case RecordImportAction.Insert:
      case RecordImportAction.Update:
      case RecordImportAction.CreateSkuAndInsert:
        return 'green';
      case RecordImportAction.Duplicate:
      case RecordImportAction.Skip:
        return 'red';
      case RecordImportAction.Delete:
        return 'orange';
      case RecordImportAction.NoChange:
      case RecordImportAction.NotSet:
        break;
    }

    return '';
  }, []);

  return (
    <ScrolledTableWrapper offset={150}>
      <InfiniteScrollTable<CalculationRecordImportItemResponse, GetCalculationImportItemsRequest>
        id="redistribution-import-items"
        rowKey="sequence"
        columns={columns}
        dataSource={data}
        setParams={setParams}
        rowClassName={rowClassProvider}
      />
    </ScrolledTableWrapper>
  );
};

export default RedistributionImportItemsTable;
